import { whatsappDigits } from "../../../shared/domain/whatsappNumber";
import { instantOf, formatSlotTimeWithZone } from "./formatting";

/**
 * A session reminder the therapist sends themselves, from their own WhatsApp.
 *
 * Nothing here sends anything: it builds a `wa.me` link that opens the chat
 * with the patient in the WhatsApp installed on the device, message already
 * written, and the therapist presses send. It always goes to the patient's
 * WhatsApp number, never to their contact phone, and never to a patient who
 * switched WhatsApp off in their portal.
 */

export { whatsappDigits };

/**
 * What the agenda's WhatsApp button does for one booking: open the chat with
 * the reminder written. The therapist sends it from their own WhatsApp, so it
 * does not depend on the patient's opt-in, which governs automatic messages.
 */
export type WhatsAppReminderAction = {
    kind: "open";
    url: string;
    /** Called as the chat is opened, so the session's timeline can note it. */
    onOpen?: () => void;
};

/** Opens the chat with `digits` in WhatsApp, with `text` ready to send. */
export function whatsappChatUrl(digits: string, text: string): string {
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export interface ReminderValues {
    name: string;
    date: string;
    time: string;
    /** Empty for a session with no link to join. */
    link: string;
}

/** The placeholders a reminder template may use, in the order they are offered. */
export const REMINDER_PLACEHOLDERS = ["name", "date", "time", "link"] as const;

/**
 * The reminder with its placeholders filled in.
 *
 * A session with no link to join — in person, or online with no platform —
 * loses the whole line that offers it, so the patient is never told to
 * "connect here:" with nothing after it. The blank lines around it collapse
 * with it.
 */
export function fillReminderTemplate(
    template: string,
    values: ReminderValues
): string {
    const kept = values.link
        ? template
        : template
              .split("\n")
              .filter((line) => !line.includes("{link}"))
              .join("\n")
              .replace(/\n{3,}/g, "\n\n");
    return REMINDER_PLACEHOLDERS.reduce(
        (text, key) => text.split(`{${key}}`).join(values[key]),
        kept
    ).trim();
}

/**
 * The session's day and time as the patient should read them: on their own
 * clock when their zone is known, else on the therapist's, always with the
 * zone named so the reading is never ambiguous.
 *
 * Null while the therapist's zone is unknown: the stored date and hour are a
 * wall-clock reading in that zone, and cannot be placed without it.
 */
export function sessionReminderWhen(
    dateKey: string,
    hour: string,
    therapistZone: string | null,
    readerZone: string | null | undefined,
    language: string
): { date: string; time: string } | null {
    if (!therapistZone) return null;
    const instant = instantOf(dateKey, hour, therapistZone);
    if (!instant) return null;

    const zone = readerZone || therapistZone;
    let date: string;
    try {
        date = new Intl.DateTimeFormat(language, {
            weekday: "long",
            day: "numeric",
            month: "long",
            timeZone: zone,
        }).format(new Date(instant));
    } catch {
        // A stored zone the runtime no longer knows: the therapist's clock
        // still gets the patient to the session.
        return sessionReminderWhen(dateKey, hour, therapistZone, null, language);
    }
    return { date, time: formatSlotTimeWithZone(instant, zone) };
}
